import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { Text } from '@atlaskit/primitives/compiled';
import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import Suspense from '@atlaskit/react-ufo/suspense';
import Tooltip from '@atlaskit/tooltip';
import { useResolveAgentAri } from '@atlassian/conversation-assistant-agent/controllers/agent-ari';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useConversation } from '../../controllers/conversation/useConversation';

import type { RecordingDisclaimer_conversationConfigurationQuery } from './__generated__/RecordingDisclaimer_conversationConfigurationQuery.graphql';
import messages from './messages';

const INTERACTION_NAME = 'rovo-conversation-assistant-footer-recording-disclaimer';

const RecordingDisclaimerContent = (): JSX.Element | null => {
	di(useGlobalConfig, useResolveAgentAri);
	const { formatMessage } = useIntl();
	const { siteId } = useGlobalConfig();
	const { selectedAgent } = useConversation();
	const { agentAri } = useResolveAgentAri({
		agentId: selectedAgent?.id ?? '',
		existingAgentAri: selectedAgent?.agentAri,
		cloudId: siteId,
	});

	const data = useLazyLoadQuery<RecordingDisclaimer_conversationConfigurationQuery>(
		graphql`
			query RecordingDisclaimer_conversationConfigurationQuery(
				$cloudId: String!
				$containerId: ID!
				$shouldFetch: Boolean!
			) {
				agentStudio_conversationConfiguration(cloudId: $cloudId, containerId: $containerId)
					@optIn(to: "AgentStudio")
					@include(if: $shouldFetch) {
					__typename
					... on AgentStudioConversationConfig {
						isEnabled
					}
					... on QueryError {
						message
					}
				}
			}
		`,
		{
			cloudId: siteId,
			containerId: agentAri ?? '',
			shouldFetch: Boolean(selectedAgent?.id && agentAri),
		},
	);

	const config = data.agentStudio_conversationConfiguration;

	if (config?.__typename === 'QueryError') {
		throw new Error(
			`RecordingDisclaimer_conversationConfigurationQuery returns QueryError: ${config.message}`,
		);
	}

	const isEnabled =
		config?.__typename === 'AgentStudioConversationConfig' ? Boolean(config.isEnabled) : false;

	if (!isEnabled) {
		return null;
	}

	return (
		<Text color="color.text.subtlest" size="small" align="center">
			<Tooltip
				content={formatMessage(messages.recordingDisclaimerTooltip)}
				position="left"
				tag="span"
			>
				<QuestionCircleIcon
					color="currentColor"
					label={formatMessage(messages.recordingDisclaimerIconLabel)}
					size="small"
				/>
			</Tooltip>{' '}
			{formatMessage(messages.recordingDisclaimerMessage)}
		</Text>
	);
};

export const RecordingDisclaimer = (): JSX.Element => {
	di(useUFOReportError);
	const reportError = useUFOReportError();

	return (
		<Suspense fallback={null} interactionName={INTERACTION_NAME}>
			<ErrorBoundary
				fallbackRender={() => null}
				onError={(error) => {
					reportError({
						name: INTERACTION_NAME,
						errorMessage: error.message,
						errorStack: error.stack,
					});
				}}
			>
				<RecordingDisclaimerContent />
			</ErrorBoundary>
		</Suspense>
	);
};
